import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import style from './Footer.module.css'
import IconWhite from '../../../assets/svgs/IconWhite'

const NAV = [
  { label: 'Home',        id: '' },
  { label: 'About',       id: 'bot-hero' },
  { label: 'Features',    id: 'features' },
  { label: 'Team',        id: 'team' },
  { label: 'Our Journey', id: 'milestones' },
  { label: 'Contact',     id: 'contact' },
]

const LinkedInIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
)

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
)

const LocationIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1 1 16 0Z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
)

const PhoneIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.99 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.9 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
)

const colVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.5, delay: i * 0.1, ease: 'easeOut' },
  }),
}

const Footer = () => {
  const navigate = useNavigate()

  const scrollTo = (id) => {
    navigate('/')
    setTimeout(() => {
      if (!id) { window.scrollTo({ top: 0, behavior: 'smooth' }); return }
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    }, 200)
  }

  const openLegal = (path) => window.open(path, '_blank', 'noopener,noreferrer')

  return (
    <footer className={style.footer}>
      {/* Decorative glow */}
      <div className={style.glow} aria-hidden="true" />

      {/* ── Pre-footer CTA ── */}
      <motion.div
        className={style.cta}
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <p className={style.ctaEyebrow}>Start today it's free</p>
        <h2 className={style.ctaHeadline}>
          Your career, <span className={style.ctaGradient}>off the feed.</span>
        </h2>
        <p className={style.ctaSub}>
          Join professionals using Chimp to cut the noise and land what's next.
        </p>
        <motion.button
          className={style.ctaBtn}
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate('/?auth=register')}
        >
          Get started free →
        </motion.button>
      </motion.div>

      <div className={style.divider} />

      {/* ── 3-column grid ── */}
      <div className={style.grid}>

        {/* Brand column */}
        <motion.div
          className={style.brandCol}
          custom={0}
          variants={colVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <div className={style.logoRow}>
            <IconWhite />
            <span className={style.logoText}>Chimp</span>
          </div>
          <p className={style.brandDesc}>
            AI-powered career clarity for professionals in Sweden. No feed. No noise. Just what matters.
          </p>
          <div className={style.socials}>
            <a
              className={style.socialLink}
              href="https://www.linkedin.com/company/chimpai/"
              target="_blank"
              rel="noreferrer"
              aria-label="LinkedIn"
            >
              <LinkedInIcon />
            </a>
            <a
              className={style.socialLink}
              href="mailto:adchimp.ai@gmail.com"
              aria-label="Email"
            >
              <MailIcon />
            </a>
          </div>
        </motion.div>

        {/* Nav column */}
        <motion.div
          className={style.col}
          custom={1}
          variants={colVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <p className={style.colHeading}>Navigate</p>
          <nav className={style.navLinks}>
            {NAV.map(({ label, id }) => (
              <button key={label} className={style.navLink} onClick={() => scrollTo(id)}>
                {label}
              </button>
            ))}
          </nav>
        </motion.div>

        {/* Contact column */}
        <motion.div
          className={style.col}
          custom={2}
          variants={colVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          <p className={style.colHeading}>Get in touch</p>
          <div className={style.contactList}>
            <div className={style.contactRow}>
              <LocationIcon />
              <span>Forskningsringen 88, 174 62 Sundbyberg</span>
            </div>
            <div className={style.contactRow}>
              <PhoneIcon />
              <span>(+46) 764 388 716</span>
            </div>
            <div className={style.contactRow}>
              <MailIcon />
              <a className={style.contactLink} href="mailto:adchimp.ai@gmail.com">
                adchimp.ai@gmail.com
              </a>
            </div>
          </div>
        </motion.div>

      </div>

      {/* ── Bottom bar ── */}
      <div className={style.bottom}>
        <p className={style.copy}>© {new Date().getFullYear()} Chimp. All rights reserved.</p>
        <div className={style.legalLinks}>
          <button className={style.legalLink} onClick={() => openLegal('/privacy-policy')}>Privacy Policy</button>
          <span className={style.dot}>·</span>
          <button className={style.legalLink} onClick={() => openLegal('/terms')}>Terms &amp; Conditions</button>
        </div>
      </div>
    </footer>
  )
}

export default Footer
